import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  SnapAudioUploadSasUrlRequestDTO,
  FileUploadSasUrlResponseDTO,
  EnqueueNoteGenerationResponseDTO,
  EnqueueNoteGenerationRequestDTO,
  GetNoteBySnapAttemptIdResponseDTO,
  SnapProgressNotificationDTO,
} from 'dtos';
import {
  SnapStates,
} from 'dtos/snapStates';
import {
  Section,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import isNil from 'lodash/isNil';
import {
  isNilOrZero,
  roundNumber,
} from '@populateui/utils';
import {
  postApiSnapGetAudioUploadUrl,
  postApiSnapEnqueueNoteGeneration,
  getApiSnapGetNoteBySnapAttemptId,
} from 'endpoints';
import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  BlockBlobClient,
} from '@azure/storage-blob';
import useSignalREffect from 'views/EMR/hooks/signalR';
import uniqueId from 'lodash/uniqueId';
import {
  useNotify,
} from 'core/hooks';
import {
  useSnapMode,
} from 'views/EMR/stores/snap';
import head from 'lodash/head';
import {
  getErrorMessage,
} from 'utils/errors';
import useFailSafeRetry from 'views/EMR/components/Snap/useFailSafeRetry';
import {
  formatDurationToTime,
} from 'views/EMR/components/Snap/SnapPopover';
import useStepTimingLogger from 'core/hooks/useStepTimeLogger';

export type SnapAttemptState = {
  snapAttemptId?: number | null;
  enableUniqueId?: string | null;
  state?: SnapStates | null;
};

type SnapNoteGenerationProps = {
  onNoteGenerated: (note: Section[]) => void;
  onNoteGenerationFailed: (error: string) => void;
};

const DEFAULT_MIME_TYPE = 'audio/webm';

const LOG_PREFIX = {
  SNAP: 'Snap',
  ON_SNAP_PROGRESS: 'OnSnapProgress',
  NOTE_GENERATED_SUCCESSFULLY: 'NoteGeneratedSuccessfully',
  NOTE_FETCH_ENABLED: 'NoteFetchEnabled',
  NOTE_FETCH_STARTED: 'NoteFetchStarted',
  NOTE_FETCH_RESPONSE: 'NoteFetchResponse',
  NOTE_FETCH_SUCCEEDED: 'NoteFetchSucceeded',
  NOTE_SUCCEEDED_MISSING_NOTE: 'NoteSucceededMissingNote',
  NOTE_FETCH_IN_PROGRESS: 'NoteFetchInProgress',
  NOTE_FETCH_STALLED: 'NoteFetchStalled',
  NOTE_FETCH_FAILED: 'NoteFetchFailed',
  NOTE_GENERATION_FAILED: 'NoteGenerationFailed',
  CANCELLED_CLOSED: 'CancelledClosed',
  AUDIO_UPLOAD_URL: 'GotAudioUploadUrl',
  BLOB_UPLOAD: 'BlobUpload',
  ENQUEUE_NOTE_GENERATION: 'EnqueuedNoteGeneration',
};

const endStates: readonly SnapStates[] = Object.freeze([
  SnapStates.GenerateNoteSucceeded,
  SnapStates.TranscriptionFailed,
  SnapStates.GenerateNoteFailed,
]);

const failedStates: readonly SnapStates[] = Object.freeze([
  SnapStates.GenerateNoteFailed,
  SnapStates.TranscriptionFailed,
]);

const completedStates: readonly SnapStates[] = Object.freeze([
  SnapStates.GenerateNoteSucceeded,
]);

const MAX_MISSING_NOTE_RETRIES = 5;
const MAX_IN_PROGRESS_RETRIES = 5;
const NOTE_FETCH_STALL_MS = 60 * 1000;

const defaultSnapAttemptState: SnapAttemptState = {
  snapAttemptId: null,
  enableUniqueId: null,
  state: null,
};

function useEnqueueNoteGeneration() {
  return useMutation< EnqueueNoteGenerationResponseDTO, Error, EnqueueNoteGenerationRequestDTO >({
    mutationFn: (data) => postApiSnapEnqueueNoteGeneration(data),
  });
}

function useGetSnapNoteBySnapAttemptId(
  snapAttemptId: number = 0,
  enableUniqueId: string | null = null,
): UseQueryResult<GetNoteBySnapAttemptIdResponseDTO, Error> {
  return useQuery<GetNoteBySnapAttemptIdResponseDTO, Error>({
    queryKey: ['snap-note-by-snap-attempt-id', snapAttemptId, enableUniqueId],
    queryFn: () => getApiSnapGetNoteBySnapAttemptId({ snapAttemptId }),
    enabled: !isNilOrZero(snapAttemptId) && !isNil(enableUniqueId),
    staleTime: 0,
  });
}

function useGetAudioUploadUrl(): UseMutationResult<
  FileUploadSasUrlResponseDTO, Error, SnapAudioUploadSasUrlRequestDTO> {
  return useMutation<FileUploadSasUrlResponseDTO, Error, SnapAudioUploadSasUrlRequestDTO>({
    mutationFn: (data) => postApiSnapGetAudioUploadUrl(data),
  });
}

export function useStartSnapNoteGeneration({
  onNoteGenerated,
  onNoteGenerationFailed,
}: SnapNoteGenerationProps) {
  const { mutateAsync: getAudioUploadUrl } = useGetAudioUploadUrl();
  const { mutateAsync: enqueueNoteGeneration } = useEnqueueNoteGeneration();
  const snapMode = useSnapMode();
  const notify = useNotify();

  const {
    start: startLog,
    log,
    end: endLog,
  } = useStepTimingLogger(LOG_PREFIX.SNAP);

  const [
    snapAttemptState,
    setSnapAttemptState,
  ] = useState<SnapAttemptState>(defaultSnapAttemptState);

  const {
    snapAttemptId,
    enableUniqueId,
    state,
  } = snapAttemptState;

  const missingNoteRetryCountRef = useRef(0);
  const inProgressRetryCountRef = useRef(0);
  const fetchStartedForUniqueIdRef = useRef<string | null>(null);

  const clearSnapAttemptState = () => {
    missingNoteRetryCountRef.current = 0;
    inProgressRetryCountRef.current = 0;
    fetchStartedForUniqueIdRef.current = null;
    setSnapAttemptState((prev) => ({
      ...prev,
      snapAttemptId: null,
      enableUniqueId: null,
      state: null,
    }));
  };

  useEffect(() => {
    const isCancelled = snapMode === 'closed'
      && !isNil(snapAttemptId)
      && !isNil(state)
      && !endStates.includes(state);

    if (isCancelled) {
      clearSnapAttemptState();
      endLog(LOG_PREFIX.CANCELLED_CLOSED);
    }
  }, [snapMode, snapAttemptId, state]);

  const {
    data: snapNote,
    error: snapNoteError,
    refetch,
    isFetching,
  } = useGetSnapNoteBySnapAttemptId(
    snapAttemptId ?? 0,
    enableUniqueId ?? null,
  );

  const failsafeRetry = useFailSafeRetry({
    jobId: snapAttemptId,
    isTerminal: !isNil(state) && endStates.includes(state),
    isCancelled: snapMode === 'closed',
    label: LOG_PREFIX.SNAP,
    onRetry: () => {
      refetch();
    },
  });

  useEffect(() => {
    if (!isNil(snapAttemptId)) {
      missingNoteRetryCountRef.current = 0;
      inProgressRetryCountRef.current = 0;
      fetchStartedForUniqueIdRef.current = null;
      failsafeRetry.start();
    }
  }, [snapAttemptId]);

  useEffect(() => {
    if (!isNil(enableUniqueId) && !isNil(snapAttemptId)) {
      startLog(`SNAP snap-${snapAttemptId}`);
    }
  }, [enableUniqueId, snapAttemptId, startLog]);

  useEffect(() => {
    if (
      !isNil(enableUniqueId)
      && isFetching
      && fetchStartedForUniqueIdRef.current !== enableUniqueId
    ) {
      fetchStartedForUniqueIdRef.current = enableUniqueId;
      log(LOG_PREFIX.NOTE_FETCH_STARTED, { snapAttemptId });
    }
  }, [enableUniqueId, isFetching, snapAttemptId, log]);

  useEffect(() => {
    if (isNil(enableUniqueId)) {
      return undefined;
    }

    const stallTimer = setTimeout(() => {
      if (isNil(snapNote) && isNil(snapNoteError)) {
        log(LOG_PREFIX.NOTE_FETCH_STALLED, {
          snapAttemptId,
          isFetching,
        }, true);
      }
    }, NOTE_FETCH_STALL_MS);

    return () => {
      clearTimeout(stallTimer);
    };
  }, [enableUniqueId, snapAttemptId, snapNote, snapNoteError, isFetching, log]);

  const startSnapNoteGeneration = async (
    encounterId: number,
    blob: Blob,
    fileName: string,
    duration?: number,
  ) => {
    startLog(`${head(fileName.split('.'))} - ${formatDurationToTime(duration ?? 0)}`);
    try {
      const audioUploadMetadata = await getAudioUploadUrl({
        fileName,
        mimeType: DEFAULT_MIME_TYPE,
        encounterId,
      });
      log(
        LOG_PREFIX.AUDIO_UPLOAD_URL,
        audioUploadMetadata?.blobPath,
      );

      const blobClient = new BlockBlobClient(audioUploadMetadata?.uploadUrl ?? '');
      await blobClient.uploadData(blob, {
        blobHTTPHeaders: { blobContentType: DEFAULT_MIME_TYPE },
        blockSize: 8 * 1024 * 1024,
        concurrency: 4,
      });
      log(`${LOG_PREFIX.BLOB_UPLOAD} ${roundNumber(blob.size / 1024)}KB`);

      const enqueueNoteGenerationResponse = await enqueueNoteGeneration({
        encounterId,
        uploadedBlobMetaData: {
          fileName,
          blobPath: audioUploadMetadata?.blobPath ?? '',
          byteCount: blob.size,
          mimeType: DEFAULT_MIME_TYPE,
          uploadedAt: new Date().toISOString(),
        },
      });
      log(
        LOG_PREFIX.ENQUEUE_NOTE_GENERATION,
        enqueueNoteGenerationResponse?.snapAttemptId,
      );

      setSnapAttemptState({
        ...defaultSnapAttemptState,
        snapAttemptId: enqueueNoteGenerationResponse?.snapAttemptId ?? null,
      });
    } catch (error) {
      onNoteGenerationFailed(error as string);
      endLog(error as string, true);
      throw error;
    }
  };

  useSignalREffect('OnSnapProgress', (data: SnapProgressNotificationDTO) => {
    const {
      snapAttemptId: id,
      state,
      errorMessage,
    } = data;

    if (id !== snapAttemptId) {
      return;
    }

    const newState = state as SnapStates;
    setSnapAttemptState((prev) => ({
      ...prev,
      state: newState,
    }));
    log(LOG_PREFIX.ON_SNAP_PROGRESS, { newState, errorMessage });

    const isCompleted = completedStates.includes(newState);
    const isFailed = failedStates.includes(newState);

    if (isCompleted) {
      const nextEnableUniqueId = uniqueId('snap-attempt-');
      setSnapAttemptState((prev) => ({
        ...prev,
        enableUniqueId: nextEnableUniqueId,
      }));
      log(LOG_PREFIX.NOTE_FETCH_ENABLED, {
        snapAttemptId: id,
        enableUniqueId: nextEnableUniqueId,
      });
      endLog(LOG_PREFIX.NOTE_GENERATED_SUCCESSFULLY);
    } else if (isFailed) {
      notify({
        message: errorMessage ?? 'Failed to generate note',
        variant: 'error',
      });
      onNoteGenerationFailed(errorMessage ?? 'Failed to generate note');
      endLog(`${LOG_PREFIX.NOTE_GENERATION_FAILED} ${errorMessage ?? 'Failed to generate note'}`, true);
      clearSnapAttemptState();
    }
  }, [snapAttemptState]);

  useEffect(() => {
    if (!isNil(snapNote) && isNil(snapNoteError)) {
      const { state: fetchedState, note, error } = snapNote;
      const sections = (note?.note?.sections ?? []) as Section[];

      log(LOG_PREFIX.NOTE_FETCH_RESPONSE, {
        fetchedState,
        hasNote: !isNil(note),
      });

      if (fetchedState === SnapStates.GenerateNoteSucceeded && !isNil(note)) {
        log(LOG_PREFIX.NOTE_FETCH_SUCCEEDED, { sectionCount: sections.length });
        onNoteGenerated(sections);
        clearSnapAttemptState();
      } else if (
        fetchedState === SnapStates.GenerateNoteSucceeded
        && isNil(note)
      ) {
        if (missingNoteRetryCountRef.current < MAX_MISSING_NOTE_RETRIES) {
          missingNoteRetryCountRef.current += 1;
          log(LOG_PREFIX.NOTE_SUCCEEDED_MISSING_NOTE, {
            retry: missingNoteRetryCountRef.current,
            snapAttemptId,
          });
          refetch();
        } else {
          const errorMessage = 'Note generation completed but note could not be retrieved';
          onNoteGenerationFailed(errorMessage);
          endLog(`${LOG_PREFIX.NOTE_FETCH_FAILED} ${errorMessage}`, true);
          clearSnapAttemptState();
          notify({ message: errorMessage, variant: 'error' });
        }
      } else if (failedStates.includes(fetchedState as SnapStates)) {
        const errorMessage = error ?? 'Failed to generate note';
        onNoteGenerationFailed(errorMessage);
        endLog(`${LOG_PREFIX.NOTE_FETCH_FAILED} ${errorMessage}`, true);
        clearSnapAttemptState();
        notify({ message: errorMessage, variant: 'error' });
      } else if (!isNil(fetchedState)) {
        log(LOG_PREFIX.NOTE_FETCH_IN_PROGRESS, {
          fetchedState,
          signalRState: state,
        });

        if (state === SnapStates.GenerateNoteSucceeded) {
          if (inProgressRetryCountRef.current < MAX_IN_PROGRESS_RETRIES) {
            inProgressRetryCountRef.current += 1;
            refetch();
          } else {
            const errorMessage = 'Note generation completed but note is not ready yet';
            onNoteGenerationFailed(errorMessage);
            endLog(`${LOG_PREFIX.NOTE_FETCH_FAILED} ${errorMessage}`, true);
            clearSnapAttemptState();
            notify({ message: errorMessage, variant: 'error' });
          }
        } else {
          setSnapAttemptState((prev) => ({
            ...prev,
            state: fetchedState as SnapStates,
          }));
        }
      }
    } else if (!isNil(snapNoteError)) {
      const errorMessage = getErrorMessage(snapNoteError);
      onNoteGenerationFailed(errorMessage as string);
      endLog(`${LOG_PREFIX.NOTE_FETCH_FAILED} ${errorMessage}`, true);
      clearSnapAttemptState();
      notify({
        message: errorMessage as string,
        variant: 'error',
      });
    }
  }, [snapNote, snapNoteError, state, snapAttemptId]);

  return {
    startSnapNoteGeneration,
    snapProgressState: snapAttemptState,
  };
}
